import { useState, useCallback, useEffect } from "react";
import { Plus, Edit2, Trash2, X, CreditCard, Gift, ChevronDown, ChevronUp, Ban, CheckCircle } from "lucide-react";
import { useAdmin } from "../admin-context";
import DataTable from "../DataTable";

/* ------------------------------------------------------------------ */
/*  Virtual card visual                                                 */
/* ------------------------------------------------------------------ */
const VirtualCard = ({ card, userName }) => {
  if (!card) return null;
  const active = card.status === "active";
  return (
    <div
      className={`relative rounded-2xl p-5 text-white overflow-hidden select-none ${
        active
          ? "bg-gradient-to-br from-orange-500 via-orange-400 to-red-500 shadow-lg shadow-orange-200"
          : "bg-gradient-to-br from-gray-400 to-gray-500 shadow-md"
      }`}
    >
      {/* decorative circles */}
      <div className="absolute -top-6 -right-6 w-28 h-28 bg-white/10 rounded-full" />
      <div className="absolute -bottom-8 -left-4 w-36 h-36 bg-white/10 rounded-full" />

      <div className="relative z-10">
        <div className="flex justify-between items-start mb-6">
          <div>
            <p className="text-[10px] font-semibold opacity-70 uppercase tracking-widest">Gamapa Loyalty</p>
            <p className="text-sm font-bold mt-0.5">Virtual Card</p>
          </div>
          <span
            className={`text-[10px] font-black px-2 py-0.5 rounded-full ${
              active ? "bg-white/20" : "bg-white/10"
            }`}
          >
            {active ? "ACTIVE" : "BLOCKED"}
          </span>
        </div>

        <p className="font-mono text-lg tracking-widest font-bold mb-5">
          {card.card_number}
        </p>

        <div className="flex justify-between items-end">
          <div>
            <p className="text-[10px] opacity-60 uppercase">Card Holder</p>
            <p className="text-sm font-bold truncate max-w-[180px]">{userName || "—"}</p>
          </div>
          <div className="text-right">
            <p className="text-[10px] opacity-60 uppercase">Member Since</p>
            <p className="text-sm font-bold">
              {card.created_at
                ? new Date(card.created_at).getFullYear()
                : new Date().getFullYear()}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Loyalty modal (card + points)                                       */
/* ------------------------------------------------------------------ */
const LoyaltyModal = ({ user, onClose, getUserLoyalty, awardPoints, updateCardStatus }) => {
  const [loading, setLoading] = useState(true);
  const [loyalty, setLoyalty] = useState(null);
  const [awardForm, setAwardForm] = useState({ points: "", description: "" });
  const [saving, setSaving] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const data = await getUserLoyalty(user.id);
    setLoyalty(data);
    setLoading(false);
  }, [user.id, getUserLoyalty]);

  useEffect(() => { load(); }, [load]);

  const handleAward = async (e) => {
    e.preventDefault();
    if (!awardForm.points) return;
    setSaving(true);
    await awardPoints(user.id, Number(awardForm.points), awardForm.description || undefined);
    setAwardForm({ points: "", description: "" });
    await load();
    setSaving(false);
  };

  const handleToggleCard = async () => {
    if (!loyalty?.card) return;
    const newStatus = loyalty.card.status === "active" ? "blocked" : "active";
    setSaving(true);
    await updateCardStatus(loyalty.card.id, newStatus);
    await load();
    setSaving(false);
  };

  const balance = loyalty?.balance ?? 0;
  const value = (balance / 20).toFixed(2);

  const typeLabel = (type) => {
    if (type === "earn")   return { text: "Earned",   cls: "bg-emerald-100 text-emerald-700" };
    if (type === "redeem") return { text: "Redeemed", cls: "bg-red-100 text-red-600" };
    return                        { text: "Manual",   cls: "bg-blue-100 text-blue-700" };
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden">
        {/* header */}
        <div className="flex justify-between items-center p-5 border-b bg-gray-50">
          <div className="flex items-center gap-2">
            <CreditCard size={18} className="text-orange-500" />
            <h3 className="text-xl font-bold text-gray-800">Loyalty Card</h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 hover:bg-gray-200 p-1 rounded-full transition"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-5 space-y-4 max-h-[80vh] overflow-y-auto">
          {/* customer name */}
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-orange-100 flex items-center justify-center text-orange-600 font-bold text-sm flex-shrink-0">
              {user.name?.charAt(0)?.toUpperCase() || "?"}
            </div>
            <div>
              <p className="font-bold text-gray-900">{user.name}</p>
              <p className="text-xs text-gray-400">{user.email}</p>
            </div>
          </div>

          {loading ? (
            <div className="py-10 text-center text-gray-400 text-sm">Loading loyalty data…</div>
          ) : (
            <>
              {/* card visual */}
              <VirtualCard card={loyalty?.card} userName={user.name} />

              {/* toggle card status */}
              <button
                onClick={handleToggleCard}
                disabled={saving}
                className={`w-full flex items-center justify-center gap-2 py-2 rounded-xl text-sm font-bold transition ${
                  loyalty?.card?.status === "active"
                    ? "bg-red-50 text-red-500 hover:bg-red-100"
                    : "bg-emerald-50 text-emerald-600 hover:bg-emerald-100"
                }`}
              >
                {loyalty?.card?.status === "active"
                  ? <><Ban size={14} /> Block Card</>
                  : <><CheckCircle size={14} /> Activate Card</>}
              </button>

              {/* points balance */}
              <div className="bg-orange-50 rounded-2xl p-4 border border-orange-100 flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 font-semibold uppercase tracking-wide">Points Balance</p>
                  <p className="text-3xl font-black text-orange-500 leading-tight">{balance.toLocaleString()}</p>
                  <p className="text-xs text-gray-400">≈ ${value} value</p>
                </div>
                <Gift size={32} className="text-orange-300" />
              </div>

              {/* award points form */}
              <form onSubmit={handleAward} className="space-y-2">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">Adjust Points</p>
                <div className="flex gap-2">
                  <input
                    type="number"
                    placeholder="Points (use − to deduct)"
                    value={awardForm.points}
                    onChange={(e) => setAwardForm((f) => ({ ...f, points: e.target.value }))}
                    className="flex-1 border border-gray-300 px-3 py-2 rounded-lg text-sm outline-none focus:ring-2 focus:ring-orange-400 focus:border-orange-400"
                  />
                  <button
                    type="submit"
                    disabled={saving || !awardForm.points}
                    className="px-4 py-2 bg-orange-500 text-white rounded-lg text-sm font-bold hover:bg-orange-600 disabled:opacity-50 transition"
                  >
                    Save
                  </button>
                </div>
                <input
                  type="text"
                  placeholder="Reason (optional)"
                  value={awardForm.description}
                  onChange={(e) => setAwardForm((f) => ({ ...f, description: e.target.value }))}
                  className="w-full border border-gray-300 px-3 py-2 rounded-lg text-sm outline-none focus:ring-2 focus:ring-orange-400 focus:border-orange-400"
                />
              </form>

              {/* points history */}
              {loyalty?.transactions?.length > 0 && (
                <div>
                  <button
                    onClick={() => setShowHistory((v) => !v)}
                    className="w-full flex items-center justify-between text-xs font-bold text-gray-500 uppercase tracking-wide py-1"
                  >
                    Points History ({loyalty.transactions.length})
                    {showHistory ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  </button>
                  {showHistory && (
                    <div className="space-y-1 mt-2 max-h-48 overflow-y-auto pr-1">
                      {loyalty.transactions.map((tx) => {
                        const { text, cls } = typeLabel(tx.type);
                        return (
                          <div key={tx.id} className="flex items-center justify-between text-sm py-1.5 border-b border-gray-50 last:border-0">
                            <div>
                              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full mr-2 ${cls}`}>{text}</span>
                              <span className="text-gray-600 text-xs">{tx.description || "—"}</span>
                            </div>
                            <span className={`font-bold text-sm ${tx.points > 0 ? "text-emerald-600" : "text-red-500"}`}>
                              {tx.points > 0 ? "+" : ""}{tx.points}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Customers page                                                      */
/* ------------------------------------------------------------------ */
const Customers = () => {
  const { users, addUser, updateUser, deleteUser, hasPermission, getUserLoyalty, awardPoints, updateCardStatus } = useAdmin();
  const canCreate = hasPermission("customers.create");
  const canEdit   = hasPermission("customers.edit");
  const canDelete = hasPermission("customers.delete");

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [loyaltyUser, setLoyaltyUser] = useState(null);

  const initialForm = { name: "", email: "", phone: "", password: "", status: "active" };
  const [formData, setFormData] = useState(initialForm);
  const field = (k, v) => setFormData((f) => ({ ...f, [k]: v }));

  const handleOpen = (user = null) => {
    if (user) {
      setEditingId(user.id);
      setFormData({
        name: user.name || "",
        email: user.email || "",
        phone: user.phone || "",
        password: "",
        status: user.status || "active",
      });
    } else {
      setEditingId(null);
      setFormData(initialForm);
    }
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (editingId) {
      const payload = { ...formData };
      if (!payload.password) delete payload.password;
      await updateUser(editingId, payload);
    } else {
      await addUser({ ...formData, license_id: 2 });
    }
    setIsModalOpen(false);
  };

  const columns = [
    {
      key: "name",
      label: "Customer",
      sortable: true,
      render: (val, row) => (
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-orange-100 flex items-center justify-center text-orange-600 font-bold text-sm flex-shrink-0">
            {val?.charAt(0)?.toUpperCase() || "?"}
          </div>
          <div className="min-w-0">
            <p className="font-bold text-gray-900 truncate">{val}</p>
            <p className="text-xs text-gray-400">{row.email}</p>
          </div>
        </div>
      ),
    },
    {
      key: "phone",
      label: "Phone",
      render: (val) => (
        <span className="text-sm text-gray-600">{val || "—"}</span>
      ),
    },
    {
      key: "status",
      label: "Status",
      sortable: true,
      render: (val) => {
        const active = val === "active";
        return (
          <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${active ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"}`}>
            {val || "—"}
          </span>
        );
      },
    },
  ];

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Customers</h1>

      <DataTable
        columns={columns}
        data={users}
        loading={!users.length}
        searchKeys={["name", "email", "phone"]}
        searchPlaceholder="Search customers..."
        filters={[
          {
            key: "status",
            label: "Status",
            options: [
              { label: "Active", value: "active" },
              { label: "Inactive", value: "inactive" },
            ],
          },
        ]}
        defaultPageSize={10}
        pageSizeOptions={[10, 25, 50]}
        emptyText="No customers found."
        headerActions={
          canCreate && (
            <button
              onClick={() => handleOpen()}
              className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-xl flex items-center gap-2 font-bold shadow-lg shadow-orange-200 transition active:scale-95 text-sm"
            >
              <Plus size={16} /> Add Customer
            </button>
          )
        }
        actions={(row) => (
          <>
            <button
              onClick={() => setLoyaltyUser(row)}
              className="p-2 hover:bg-orange-50 rounded-lg text-orange-400 transition"
              title="Loyalty Card"
            >
              <CreditCard size={15} />
            </button>
            {canEdit && (
              <button
                onClick={() => handleOpen(row)}
                className="p-2 hover:bg-orange-50 rounded-lg text-orange-500 transition"
                title="Edit"
              >
                <Edit2 size={15} />
              </button>
            )}
            {canDelete && (
              <button
                onClick={() => deleteUser(row.id)}
                className="p-2 hover:bg-red-50 rounded-lg text-red-400 transition"
                title="Delete"
              >
                <Trash2 size={15} />
              </button>
            )}
          </>
        )}
      />

      {/* Edit / Add modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-200">
            <div className="flex justify-between items-center p-5 border-b bg-gray-50">
              <h3 className="text-xl font-bold text-gray-800">{editingId ? "Edit Customer" : "Add Customer"}</h3>
              <button onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:text-gray-600 hover:bg-gray-200 p-1 rounded-full transition">
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Full Name</label>
                <input
                  required
                  value={formData.name}
                  onChange={(e) => field("name", e.target.value)}
                  className="w-full border border-gray-300 px-3 py-2.5 rounded-lg outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition"
                  placeholder="e.g. John Doe"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Email</label>
                <input
                  required
                  type="email"
                  value={formData.email}
                  onChange={(e) => field("email", e.target.value)}
                  className="w-full border border-gray-300 px-3 py-2.5 rounded-lg outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition"
                  placeholder="e.g. john@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Phone</label>
                <input
                  required={!editingId}
                  value={formData.phone}
                  onChange={(e) => field("phone", e.target.value)}
                  className="w-full border border-gray-300 px-3 py-2.5 rounded-lg outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition"
                  placeholder="e.g. +1 555 000 0000"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">
                  Password {editingId && <span className="text-gray-400 font-normal">(leave blank to keep current)</span>}
                </label>
                <input
                  required={!editingId}
                  type="password"
                  value={formData.password}
                  onChange={(e) => field("password", e.target.value)}
                  className="w-full border border-gray-300 px-3 py-2.5 rounded-lg outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition"
                  placeholder="••••••••"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => field("status", e.target.value)}
                  className="w-full border border-gray-300 px-3 py-2.5 rounded-lg outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition bg-white"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="flex-1 py-2.5 border border-gray-300 rounded-lg font-bold text-gray-700 hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 py-2.5 bg-orange-500 text-white rounded-lg font-bold hover:bg-orange-600 shadow-md transition"
                >
                  {editingId ? "Update" : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Loyalty modal */}
      {loyaltyUser && (
        <LoyaltyModal
          user={loyaltyUser}
          onClose={() => setLoyaltyUser(null)}
          getUserLoyalty={getUserLoyalty}
          awardPoints={awardPoints}
          updateCardStatus={updateCardStatus}
        />
      )}
    </div>
  );
};

export default Customers;
